import { useTranslation } from 'react-i18next'
import { FilterButton, FilterField } from '@/components/FilterButton'
import { SearchSelect } from '@/components/SearchSelect'
import type { ProductFeedFacet, ProductFeedFacets } from '@/services/productFeed/productFeedApi'
import { formatNumber } from '@/utils/format'

export interface RetailProductFilterState {
  /** Merchant id as a string, or '' for every merchant. */
  merchantId: string
  brand: string
  colour: string
}

export const NO_RETAIL_PRODUCT_FILTERS: RetailProductFilterState = {
  merchantId: '',
  brand: '',
  colour: '',
}

interface RetailProductFiltersProps {
  filters: RetailProductFilterState
  onChange: (filters: RetailProductFilterState) => void
  facets: ProductFeedFacets | null | undefined
  loading?: boolean
}

/** Merchant, brand and colour filters for a retail product list, in one button. */
export function RetailProductFilters({
  filters,
  onChange,
  facets,
  loading,
}: RetailProductFiltersProps) {
  const { t } = useTranslation()
  const merchants = facets?.merchants ?? []

  const facetOptions = (list: ProductFeedFacet[] | undefined) =>
    (list ?? []).map((f) => ({ value: f.value, label: `${f.value} (${formatNumber(f.count)})` }))

  const activeCount = Object.values(filters).filter(Boolean).length

  return (
    <FilterButton activeCount={activeCount} onReset={() => onChange(NO_RETAIL_PRODUCT_FILTERS)}>
      {/* One feed has nothing to narrow, but a remembered pick stays visible so it can be cleared. */}
      {(merchants.length > 1 || !!filters.merchantId) && (
        <FilterField label={t('retailProducts.merchant')}>
          <SearchSelect
            value={filters.merchantId}
            onChange={(merchantId) => onChange({ ...filters, merchantId })}
            options={merchants.map((m) => ({ value: String(m.id), label: m.title || m.link }))}
            placeholder={t('common.all')}
            clearable
            loading={loading}
          />
        </FilterField>
      )}
      <FilterField label={t('retailProducts.brand')}>
        <SearchSelect
          value={filters.brand}
          onChange={(brand) => onChange({ ...filters, brand })}
          options={facetOptions(facets?.brands)}
          placeholder={t('common.all')}
          fallbackLabel={filters.brand}
          clearable
          loading={loading}
        />
      </FilterField>
      <FilterField label={t('retailProducts.colour')}>
        <SearchSelect
          value={filters.colour}
          onChange={(colour) => onChange({ ...filters, colour })}
          options={facetOptions(facets?.colours)}
          placeholder={t('common.all')}
          fallbackLabel={filters.colour}
          clearable
          loading={loading}
        />
      </FilterField>
    </FilterButton>
  )
}
